import React from 'react';
import { Box, Chip, Link, Paper, Typography } from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { PullRequestDetails, isRelevantStatusCheck } from '@finvari/deliver';
import { UnitStatus } from '../../../workflow/workflowConfig';

interface UnitTestsTabProps {
  pr: PullRequestDetails;
  status: UnitStatus;
}

// The unit lane is GitHub's own checks (it has its own rules); we just surface the relevant runs.
const UnitTestsTab: React.FC<UnitTestsTabProps> = ({ pr, status }) => {
  const checks = (pr.statuses ?? []).filter(isRelevantStatusCheck);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="body2" color="text.secondary">
        Unit tests run on GitHub. {status === 'na'
          ? 'No relevant checks are reported for this PR.'
          : status === 'needed'
            ? 'GitHub has not registered any check runs for the latest commit, so the tests have not started.'
            : `Overall: ${status}.`}
      </Typography>
      {checks.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No GitHub status checks available.</Typography>
      ) : (
        checks.map((check, i) => (
          <Paper key={i} variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              size="small"
              label={check.conclusion ?? check.status}
              color={
                check.conclusion === 'success' ? 'success' :
                check.conclusion === 'failure' ? 'error' :
                (check.status === 'in_progress' || check.status === 'queued') ? 'warning' : 'default'
              }
            />
            <Typography variant="body2" sx={{ flexGrow: 1 }}>{check.name}</Typography>
            {check.details_url && (
              <Link href={check.details_url} target="_blank" rel="noopener noreferrer">
                <OpenInNew fontSize="small" />
              </Link>
            )}
          </Paper>
        ))
      )}
    </Box>
  );
};

export default UnitTestsTab;
